'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize, ZoomIn, ZoomOut } from 'lucide-react';
import type { CecopLiveEntry, CecopMapSnapshot, CecopPoint, CecopViewport } from '@/lib/types';
import { cn } from '@/lib/utils';

// ─── Conversión píxel(%) ↔ coordenadas de Minecraft ──────────────────────────
// Misma interpolación lineal que usa el mapa del servidor: dos esquinas opuestas
// calibradas (píxel en % de la imagen ↔ bloque MC) bastan para situar cualquier punto.

export type Calib = { xPct: number; yPct: number; mcX: number; mcZ: number };

export function mcToPct(mcX: number, mcZ: number, calib?: Calib[]): { x: number; y: number } | null {
  if (!calib || calib.length < 2) return null;
  const [c0, c1] = calib;
  const dX = c1.mcX - c0.mcX;
  const dZ = c1.mcZ - c0.mcZ;
  if (!dX || !dZ) return null;
  return {
    x: c0.xPct + ((mcX - c0.mcX) * (c1.xPct - c0.xPct)) / dX,
    y: c0.yPct + ((mcZ - c0.mcZ) * (c1.yPct - c0.yPct)) / dZ,
  };
}

export function pctToMc(xPct: number, yPct: number, calib?: Calib[]): CecopPoint | null {
  if (!calib || calib.length < 2) return null;
  const [c0, c1] = calib;
  const dxPct = c1.xPct - c0.xPct;
  const dyPct = c1.yPct - c0.yPct;
  if (!dxPct || !dyPct) return null;
  return {
    x: Math.round(c0.mcX + ((xPct - c0.xPct) * (c1.mcX - c0.mcX)) / dxPct),
    z: Math.round(c0.mcZ + ((yPct - c0.yPct) * (c1.mcZ - c0.mcZ)) / dyPct),
  };
}

/**
 * Un recorrido del programa (puntos en % de la imagen, que es como los guarda
 * `dias_ss`) traído al espacio de coordenadas de Minecraft, que es donde vive
 * todo lo demás del CECOP. Los puntos que no se puedan convertir se descartan.
 */
export function recorridoAMc(puntos: { x: number; y: number }[] | undefined, calib?: Calib[]): CecopPoint[] {
  if (!Array.isArray(puntos)) return [];
  return puntos
    .map((p) => pctToMc(p.x, p.y, calib))
    .filter((p): p is CecopPoint => !!p);
}

export function isCalibrated(map?: CecopMapSnapshot | null): boolean {
  const c = map?.calib;
  return !!(map?.imageUrl && c && c.length === 2 && c[1].mcX - c[0].mcX !== 0 && c[1].mcZ - c[0].mcZ !== 0);
}

// ─── Componente ──────────────────────────────────────────────────────────────

/**
 * Un recorrido más de los que se dibujan a la vez (CECOP de Semana Santa: todas
 * las cofradías del día sobre el mismo mapa). Va en coordenadas de Minecraft,
 * igual que el recorrido principal.
 */
export interface ProcesionRuta {
  id: string;
  color: string;
  ida?: CecopPoint[];
  vuelta?: CecopPoint[];
  iglesia?: CecopPoint | null;
  estacion?: CecopPoint | null;
  /** Apagada: se dibuja tenue (las demás cofradías cuando hay una seleccionada). */
  atenuada?: boolean;
}

export interface ProcesionMapProps {
  map: CecopMapSnapshot | null;
  /** Recorte a mostrar (en % de la imagen base). `null` = mapa completo. */
  viewport?: CecopViewport | null;
  color: string;
  /** Varios recorridos a la vez. Se pintan DEBAJO del recorrido principal. */
  rutas?: ProcesionRuta[];
  iglesia?: CecopPoint | null;
  estacion?: CecopPoint | null;
  ida?: CecopPoint[];
  vuelta?: CecopPoint[];
  live?: CecopLiveEntry[];
  /** Clic sobre el mapa (solo si se pasa). Devuelve % de la imagen base y coordenada MC. */
  onMapClick?: (pos: { xPct: number; yPct: number; mc: CecopPoint | null }) => void;
  /** Activa el arrastre para seleccionar un rectángulo (recorte del GPS). */
  dragSelect?: boolean;
  onDragSelect?: (rect: CecopViewport) => void;
  /**
   * MODO MOVER: arrastrar sobre el mapa emite el desplazamiento **en % de la imagen**,
   * poquito a poco (el incremento de cada movimiento del ratón, no el total). Lo usa el
   * editor del día para correr el recorrido de UNA cofradía hasta encajarlo.
   *
   * <p>Manda sobre el paneo de `interactive`: mientras está puesto, arrastrar mueve el
   * recorrido y no el mapa.</p>
   */
  moveDrag?: boolean;
  onMoveDrag?: (delta: { dx: number; dy: number }) => void;
  /** Puntos de la ruta clicables (para borrarlos en el editor). */
  onPointClick?: (kind: 'ida' | 'vuelta', index: number) => void;
  showPointHandles?: boolean;
  className?: string;
  compact?: boolean;
  /** Altura fija en px: el ancho lo deduce la proporción del recorte. */
  fixedHeight?: number;
  /** Escribe el nombre del puesto (Cruz de guía, Paso 1…) bajo su círculo, siempre. */
  showLabels?: boolean;
  /**
   * Igual que {@link showLabels} pero SOLO al pasar el ratón por encima del
   * marcador. Es lo que se usa en la portada: con un día entero de cofradías,
   * las etiquetas fijas tapan el mapa, pero al apuntar una hace falta saber
   * quién es.
   */
  hoverLabels?: boolean;
  /**
   * Marcador apuntado con el ratón (o `null` al salir). Lo usa el panel público
   * para enseñar al lado el cuadro de horarios de esa cofradía.
   */
  onMarkerHover?: (entry: CecopLiveEntry | null) => void;
  /** Permite acercar (rueda / botones) y arrastrar para moverse por el mapa. */
  interactive?: boolean;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 8;

/** Filete del marcador de escudo: el dorado de la web (`JARTI_AMARILLO`). */
export const ESCUDO_RING = '#fbd641';

function clampView(v: { s: number; tx: number; ty: number }) {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.s));
  // El contenido escalado nunca deja hueco: el desplazamiento va de -(s-1) a 0
  // en fracción del lado del contenedor.
  const lim = s - 1;
  return {
    s,
    tx: Math.min(0, Math.max(-lim, v.tx)),
    ty: Math.min(0, Math.max(-lim, v.ty)),
  };
}

/**
 * Leyenda compartida: ida (sólida) y vuelta (discontinua).
 *
 * <p>La <b>línea del cortejo</b> (una línea de otro color uniendo la cruz de guía
 * con los pasos, calcada sobre el recorrido) se quitó por completo el 2026-09-02
 * a petición del usuario: ensuciaba el mapa y no aportaba nada que no dijeran ya
 * los propios marcadores. No reimplementar.</p>
 */
export function ProcesionLegend({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  const item = (stroke: string, label: string, dashed?: boolean) => (
    <span key={label} className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase tracking-widest text-white/85">
      <svg width="26" height="7" className="shrink-0">
        <line
          x1="1" y1="3.5" x2="25" y2="3.5"
          stroke={stroke}
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={dashed ? '6 4' : undefined}
          opacity={dashed ? 0.8 : 1}
        />
      </svg>
      {label}
    </span>
  );
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5', className)}>
      {item(color, 'Ida')}
      {item(color, 'Vuelta', true)}
    </div>
  );
}

const FULL: CecopViewport = { x: 0, y: 0, w: 100, h: 100 };

export function ProcesionMap({
  map,
  viewport,
  color,
  rutas,
  iglesia,
  estacion,
  ida = [],
  vuelta = [],
  live = [],
  onMapClick,
  dragSelect,
  onDragSelect,
  moveDrag,
  onMoveDrag,
  onPointClick,
  showPointHandles,
  className,
  compact,
  fixedHeight,
  showLabels,
  hoverLabels,
  onMarkerHover,
  interactive,
}: ProcesionMapProps) {
  // Marcador apuntado con el ratón (clave = índice en `live`).
  const [hover, setHover] = useState<number | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Zoom/paneo (solo en modo interactivo). `s` es la escala y `tx`/`ty` el
  // desplazamiento en fracción del lado del contenedor.
  const [view, setView] = useState({ s: 1, tx: 0, ty: 0 });
  const panRef = useRef<{ id: number; x: number; y: number; tx: number; ty: number } | null>(null);
  const [panning, setPanning] = useState(false);
  /** Modo MOVER: último punto del ratón en % de la imagen, para emitir incrementos. */
  const moveRef = useRef<{ id: number; xPct: number; yPct: number } | null>(null);

  const vp = viewport ?? FULL;
  const calib = map?.calib as Calib[] | undefined;

  // La rueda se escucha a mano (no en `onWheel`) porque React registra los
  // listeners de wheel como pasivos y `preventDefault()` no haría nada: la
  // página scrollearía al intentar acercar el mapa.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !interactive) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      setView((v) => {
        const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.s * (e.deltaY < 0 ? 1.18 : 1 / 1.18)));
        // Se mantiene bajo el cursor el mismo punto del mapa al acercar.
        return clampView({ s, tx: px - (px - v.tx) * (s / v.s), ty: py - (py - v.ty) * (s / v.s) });
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [interactive]);

  const zoomBy = (factor: number) =>
    setView((v) => {
      const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.s * factor));
      // Se acerca respecto al centro del recuadro.
      return clampView({ s, tx: 0.5 - (0.5 - v.tx) * (s / v.s), ty: 0.5 - (0.5 - v.ty) * (s / v.s) });
    });

  // El recorte deforma la relación de aspecto: se calcula con el tamaño natural
  // de la imagen (conocido tras cargarla) para que el contenedor case exacto.
  const ratio = nat && vp.w > 0 && vp.h > 0 ? (vp.w * nat.w) / (vp.h * nat.h) : 16 / 9;

  const posOf = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const el = boxRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      // Coordenadas dentro del recorte → % sobre la imagen base.
      const xPct = vp.x + ((e.clientX - r.left) / r.width) * vp.w;
      const yPct = vp.y + ((e.clientY - r.top) / r.height) * vp.h;
      return { xPct, yPct };
    },
    [vp],
  );

  const handleClick = (e: React.MouseEvent) => {
    if (!onMapClick || dragSelect) return;
    const p = posOf(e);
    if (!p) return;
    onMapClick({ ...p, mc: pctToMc(p.xPct, p.yPct, calib) });
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    // MOVER manda sobre todo lo demás: mientras está puesto, arrastrar corre el
    // recorrido y no el mapa.
    if (moveDrag) {
      const p = posOf(e);
      if (!p) return;
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      moveRef.current = { id: e.pointerId, xPct: p.xPct, yPct: p.yPct };
      return;
    }
    if (interactive && !dragSelect) {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      panRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
      setPanning(true);
      return;
    }
    if (!dragSelect) return;
    const p = posOf(e);
    if (!p) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ x0: p.xPct, y0: p.yPct, x1: p.xPct, y1: p.yPct });
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    const mov = moveRef.current;
    if (mov && mov.id === e.pointerId) {
      const p = posOf(e);
      if (!p) return;
      // Se emite el INCREMENTO, no el total: así el que escucha solo tiene que sumar y
      // no necesita saber dónde empezó el arrastre.
      onMoveDrag?.({ dx: p.xPct - mov.xPct, dy: p.yPct - mov.yPct });
      moveRef.current = { id: mov.id, xPct: p.xPct, yPct: p.yPct };
      return;
    }
    const pan = panRef.current;
    if (pan && pan.id === e.pointerId) {
      const r = boxRef.current?.getBoundingClientRect();
      if (!r) return;
      setView((v) =>
        clampView({ ...v, tx: pan.tx + (e.clientX - pan.x) / r.width, ty: pan.ty + (e.clientY - pan.y) / r.height }),
      );
      return;
    }
    if (!dragSelect || !drag) return;
    const p = posOf(e);
    if (!p) return;
    setDrag((d) => (d ? { ...d, x1: p.xPct, y1: p.yPct } : d));
  };
  const handlePointerUp = () => {
    if (moveRef.current) {
      moveRef.current = null;
      return;
    }
    if (panRef.current) {
      panRef.current = null;
      setPanning(false);
    }
    if (!dragSelect || !drag) return;
    const x = Math.min(drag.x0, drag.x1);
    const y = Math.min(drag.y0, drag.y1);
    const w = Math.abs(drag.x1 - drag.x0);
    const h = Math.abs(drag.y1 - drag.y0);
    setDrag(null);
    // Un arrastre minúsculo suele ser un clic accidental → se ignora.
    if (w > 2 && h > 2 && onDragSelect) {
      onDragSelect({
        x: Math.max(0, x),
        y: Math.max(0, y),
        w: Math.min(100 - Math.max(0, x), w),
        h: Math.min(100 - Math.max(0, y), h),
      });
    }
  };

  if (!map?.imageUrl) {
    return (
      <div className={cn('grid place-items-center rounded-2xl border border-dashed border-white/10 py-16 text-sm text-muted-foreground', className)}>
        No hay mapa del servidor configurado.
      </div>
    );
  }

  const toPct = (p: CecopPoint) => mcToPct(p.x, p.z, calib);
  const line = (pts: CecopPoint[]) =>
    pts.map(toPct).filter(Boolean).map((p) => `${(p as any).x},${(p as any).y}`).join(' ');

  const markerSize = compact ? 'h-4 w-4' : 'h-6 w-6';

  return (
    <div
      ref={boxRef}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={cn(
        'relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 select-none',
        (onMapClick || dragSelect) && 'cursor-crosshair',
        moveDrag && 'cursor-move touch-none',
        interactive && !dragSelect && !moveDrag
          && (panning ? 'cursor-grabbing touch-none' : 'cursor-grab touch-none'),
        className,
      )}
      // Con `fixedHeight` manda la altura y el ancho lo deduce la proporción del
      // recorte (un recorte vertical ocupa poco ancho en vez de estirarse).
      style={
        fixedHeight
          ? { height: `${fixedHeight}px`, aspectRatio: String(ratio), width: 'auto', maxWidth: '100%' }
          : { aspectRatio: String(ratio) }
      }
    >
      {/* Escenario: aplica el zoom/paneo del modo interactivo sobre todo el mapa. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: `translate(${view.tx * 100}%, ${view.ty * 100}%) scale(${view.s})`,
          transformOrigin: '0 0',
        }}
      >
      {/* Capa interior: la imagen base a escala, desplazada para mostrar el recorte. */}
      <div
        style={{
          position: 'absolute',
          left: `${(-vp.x * 100) / vp.w}%`,
          top: `${(-vp.y * 100) / vp.h}%`,
          width: `${10000 / vp.w}%`,
          height: `${10000 / vp.h}%`,
        }}
      >
        <img
          src={map.imageUrl}
          alt="Mapa del servidor"
          draggable={false}
          onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          className="absolute inset-0 h-full w-full select-none"
        />

        {/* Recorrido. `non-scaling-stroke` mantiene el grosor pese a la deformación del viewBox. */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full">
          {/* Recorridos de las demás cofradías del día (CECOP de Semana Santa).
              Van los primeros para que el recorrido principal quede encima. */}
          {(rutas || []).map((r) => {
            const op = r.atenuada ? 0.25 : 1;
            return (
              <g key={r.id} opacity={op}>
                {(r.vuelta || []).length > 1 && (
                  <polyline
                    points={line(r.vuelta as CecopPoint[])}
                    fill="none"
                    stroke={r.color}
                    strokeWidth={compact ? 3 : 5}
                    strokeDasharray="10 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={0.75}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {(r.ida || []).length > 1 && (
                  <polyline
                    points={line(r.ida as CecopPoint[])}
                    fill="none"
                    stroke={r.color}
                    strokeWidth={compact ? 4 : 6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </g>
            );
          })}

          {vuelta.length > 1 && (
            <polyline
              points={line(vuelta)}
              fill="none"
              stroke={color}
              strokeWidth={compact ? 4 : 6}
              strokeDasharray="10 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.75}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {ida.length > 1 && (
            <polyline
              points={line(ida)}
              fill="none"
              stroke={color}
              strokeWidth={compact ? 5 : 7}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

        </svg>

        {/* Tiradores de los puntos del recorrido (solo en el editor). */}
        {showPointHandles &&
          (['ida', 'vuelta'] as const).flatMap((kind) =>
            (kind === 'ida' ? ida : vuelta).map((p, i) => {
              const pct = toPct(p);
              if (!pct) return null;
              return (
                <button
                  key={`${kind}-${i}`}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPointClick?.(kind, i); }}
                  title={`${kind === 'ida' ? 'Ida' : 'Vuelta'} · punto ${i + 1} (${p.x}, ${p.z}) — clic para borrar`}
                  className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/80 hover:scale-150 transition-transform"
                  style={{ left: `${pct.x}%`, top: `${pct.y}%`, background: kind === 'ida' ? color : '#000000aa', zIndex: 15 }}
                />
              );
            }),
          )}

        {/* Iglesias de salida de las demás cofradías del día. */}
        {(rutas || []).map((r) => {
          const pct = r.iglesia ? toPct(r.iglesia) : null;
          if (!pct) return null;
          return (
            <Marker key={`ig-${r.id}`} pct={pct} title={`Salida (${r.iglesia!.x}, ${r.iglesia!.z})`} z={8}>
              <div
                className="h-2.5 w-2.5 rounded-full border-2 border-white/80"
                style={{ background: r.color, opacity: r.atenuada ? 0.35 : 1 }}
              />
            </Marker>
          );
        })}

        {/* Iglesia (salida) */}
        {iglesia && toPct(iglesia) && (
          <Marker pct={toPct(iglesia)!} title={`Iglesia (${iglesia.x}, ${iglesia.z})`}>
            <div className={cn('grid place-items-center rounded-full border-2 border-white bg-emerald-500 text-white shadow-lg', markerSize)}>
              <span className="text-[0.55rem] font-black leading-none">⛪</span>
            </div>
          </Marker>
        )}

        {/* Estación de penitencia */}
        {estacion && toPct(estacion) && (
          <Marker pct={toPct(estacion)!} title={`Estación de penitencia (${estacion.x}, ${estacion.z})`}>
            <div className={cn('grid place-items-center rounded-full border-2 border-white bg-rose-600 text-white shadow-lg', markerSize)}>
              <span className="text-[0.55rem] font-black leading-none">✝</span>
            </div>
          </Marker>
        )}

        {/* Posiciones en vivo */}
        {live.map((e, i) => {
          if (!e.online || !Number.isFinite(e.x as number) || !Number.isFinite(e.z as number)) return null;
          const pct = mcToPct(e.x as number, e.z as number, calib);
          if (!pct) return null;
          const isCruz = e.role === 'cruz';
          const seguible = !!(hoverLabels || onMarkerHover);
          const apuntado = hover === i;
          return (
            <Marker
              key={`${e.uuid || e.name}-${i}`}
              pct={pct}
              title={`${e.label} — ${e.name} (${e.x}, ${e.z})`}
              z={apuntado ? 25 : 20}
              // Solo estos marcadores capturan el ratón, y solo cuando alguien
              // escucha: el mapa sigue arrastrándose y haciendo zoom igual.
              hoverable={seguible}
              onEnter={() => { setHover(i); onMarkerHover?.(e); }}
              onLeave={() => { setHover((h) => (h === i ? null : h)); onMarkerHover?.(null); }}
            >
              {/* El círculo queda centrado en el punto: la etiqueta va en
                  absoluto para no alterar la altura del marcador. */}
              <div className="relative">
                <div className="relative">
                  <span
                    className="absolute inset-0 animate-ping rounded-full opacity-60"
                    style={{ background: e.iconUrl ? (e.ringColor || ESCUDO_RING) : isCruz ? '#ffffff' : color }}
                  />
                  {e.iconUrl ? (
                    // Marcador con ESCUDO: círculo negro con filete dorado, que
                    // es lo que se distingue de un vistazo sobre el mapa. El
                    // puesto (cruz o nº de paso) va en una chapita en la esquina.
                    <div
                      className={cn(
                        'relative grid place-items-center overflow-hidden rounded-full shadow-lg',
                        compact ? 'h-6 w-6 border-2' : 'h-9 w-9 border-[3px]',
                      )}
                      style={{ background: '#0a0a0c', borderColor: e.ringColor || ESCUDO_RING }}
                    >
                      <img
                        src={e.iconUrl}
                        alt=""
                        draggable={false}
                        className={cn('object-contain', compact ? 'h-4 w-4' : 'h-6 w-6')}
                      />
                      <span
                        className={cn(
                          'absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full font-black leading-none text-black shadow',
                          compact ? 'h-2.5 w-2.5 text-[0.4rem]' : 'h-3.5 w-3.5 text-[0.5rem]',
                        )}
                        style={{ background: e.ringColor || ESCUDO_RING }}
                      >
                        {isCruz ? '✚' : e.index}
                      </span>
                    </div>
                  ) : (
                  <div
                    className={cn(
                      'relative grid place-items-center rounded-full border-2 shadow-lg',
                      compact ? 'h-4 w-4' : 'h-6 w-6',
                    )}
                    style={{ background: isCruz ? '#ffffff' : color, borderColor: isCruz ? color : '#ffffff' }}
                  >
                    <span
                      className={cn('font-black leading-none', compact ? 'text-[0.45rem]' : 'text-[0.6rem]')}
                      style={{ color: isCruz ? color : '#0b0b0b' }}
                    >
                      {isCruz ? '✚' : e.index}
                    </span>
                  </div>
                  )}
                </div>
                {/* Rótulo bajo el círculo: fijo con `showLabels` (el editor) o
                    solo al apuntarlo con `hoverLabels` (la portada, donde un día
                    entero de cofradías con las etiquetas puestas tapa el mapa). */}
                {(showLabels || (hoverLabels && apuntado)) && e.label && (
                  <span
                    className={cn(
                      'pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded border px-1.5 py-px font-bold leading-tight text-white shadow',
                      apuntado ? 'border-white/60 bg-black/95' : 'border-white/25 bg-black/80',
                      compact ? 'text-[0.5rem]' : 'text-[0.6rem]',
                    )}
                  >
                    {e.label}
                  </span>
                )}
              </div>
            </Marker>
          );
        })}
      </div>
      </div>

      {/* Controles de zoom (solo interactivo) */}
      {interactive && (
        <div className="absolute bottom-2 right-2 z-30 flex flex-col gap-1 rounded-lg border border-white/15 bg-background/85 p-1 backdrop-blur-sm">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); zoomBy(1.4); }}
            disabled={view.s >= ZOOM_MAX}
            title="Acercar"
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); zoomBy(1 / 1.4); }}
            disabled={view.s <= ZOOM_MIN}
            title="Alejar"
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setView({ s: 1, tx: 0, ty: 0 }); }}
            disabled={view.s === 1}
            title="Ver todo"
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <Maximize className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Rectángulo del recorte que se está arrastrando */}
      {drag && (
        <div
          className="pointer-events-none absolute border-2 border-amber-300 bg-amber-300/20"
          style={{
            left: `${((Math.min(drag.x0, drag.x1) - vp.x) * 100) / vp.w}%`,
            top: `${((Math.min(drag.y0, drag.y1) - vp.y) * 100) / vp.h}%`,
            width: `${(Math.abs(drag.x1 - drag.x0) * 100) / vp.w}%`,
            height: `${(Math.abs(drag.y1 - drag.y0) * 100) / vp.h}%`,
          }}
        />
      )}
    </div>
  );
}

function Marker({
  pct, title, children, z = 10, hoverable, onEnter, onLeave,
}: {
  pct: { x: number; y: number };
  title: string;
  children: React.ReactNode;
  z?: number;
  /** Captura el ratón (para el rótulo al pasar por encima). Por defecto NO. */
  hoverable?: boolean;
  onEnter?: () => void;
  onLeave?: () => void;
}) {
  return (
    <div
      className={cn(
        'group absolute -translate-x-1/2 -translate-y-1/2',
        hoverable ? 'pointer-events-auto cursor-help' : 'pointer-events-none',
      )}
      style={{ left: `${pct.x}%`, top: `${pct.y}%`, zIndex: z }}
      title={title}
      onMouseEnter={hoverable ? onEnter : undefined}
      onMouseLeave={hoverable ? onLeave : undefined}
    >
      {children}
    </div>
  );
}
